import { getValue, passInId, passInSystemType } from './shared';

import { createSelector } from '@reduxjs/toolkit';
import { selectInspectionsByAssetId } from '../../inspections/selectors';
import {
	selectRiskBase,
	selectRiskScoreByAssetId,
	selectSpatialLayers,
} from '../../risk/selectors';

export const selectAssetBase = createSelector(
	[getValue],
	moduleState => moduleState?.asset,
);

export const selectAssets = createSelector(
	[selectAssetBase],
	baseState => baseState?.assets ?? [],
);

export const selectMultipleAssetRequestsStatus = createSelector(
	[selectAssetBase],
	baseState => baseState?.multipleAssetRequestsStatus,
);

export const selectAssetById = createSelector(
	[selectAssets, passInId],
	(assets, selectedItem) => assets.find(({ _id }) => _id === selectedItem),
);

export const selectSensors = createSelector(
	[selectAssetBase],
	baseState => baseState?.sensors ?? [],
);

export const selectSensorById = createSelector(
	[selectSensors, passInId],
	(assets, selectedItem) => assets.find(({ _id }) => _id === selectedItem),
);

export const selectExtendedAssetById = createSelector(
	[selectAssetById, selectRiskScoreByAssetId, selectInspectionsByAssetId],
	(asset, risk, inspections) => {
		if (!asset) return undefined;
		return {
			...asset,
			risk,
			inspections,
		};
	},
);

export const selectSystemAssets = createSelector(
	[selectAssetBase],
	baseState => baseState?.systemAssets ?? {},
);

export const selectSystemTypes = createSelector(
	[selectSystemAssets],
	systemAssets =>
		Object.keys(systemAssets).filter(systemType => systemType !== 'null'),
);

export const selectAssetTypesInSystem = createSelector(
	[selectSystemAssets, passInSystemType],
	(systemAssets, systemType) =>
		systemType ? systemAssets?.[systemType] : undefined,
);

export const selectDataTypes = createSelector(
	[selectSystemTypes, selectSpatialLayers],
	(systemTypes, spatialLayers) =>
		spatialLayers.length > 0
			? [...systemTypes, 'SpatialData']
			: systemTypes,
);

const passInDataType = (
	_: Parameters<typeof getValue>[0],
	systemType: string | null,
): string | null => systemType;

export const selectLayersNamesInDataType = createSelector(
	[selectSystemAssets, selectSpatialLayers, passInDataType],
	(assetTypes, spatialLayers, dataType) => {
		if (dataType === 'SpatialData')
			return spatialLayers.map(({ LAYER_NAME }) => LAYER_NAME);
		if (dataType) return assetTypes?.[dataType];
		return undefined;
	},
);

export const selectAreDataTypesLoaded = createSelector(
	[selectAssetBase, selectRiskBase],
	(assetBase, riskBase) =>
		assetBase?.systemAssetsStatus === 'success' &&
		riskBase?.spatialLayersStatus === 'success',
);
